import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { Eyebrow, Reveal } from "./primitives";
import { api, errMsg } from "../../lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const INVALID = "Please enter a valid email address.";
const THANKS = "Thank you — you're on the list. A welcome note is on its way.";

export default function Newsletter({ className = "bg-[linear-gradient(to_bottom,#EADFCF,#F1E9DD)] pt-6" }) {
  const reduce = useReducedMotion();
  const { pathname } = useLocation();
  const mountedAt = useRef(Date.now());
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!v || !EMAIL_RE.test(v)) return setError(INVALID);
    setError("");
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email: v, source: pathname, hp, elapsed_ms: Date.now() - mountedAt.current });
      setDone(true);
    } catch (err) {
      setError(err.response?.status === 422 ? INVALID : errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="newsletter" aria-labelledby="newsletter-title" data-testid="newsletter-section" className={`scroll-mt-24 px-4 pb-24 sm:px-8 lg:px-12 lg:pb-32 ${className}`}>
      <Reveal className="relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] border border-ink/[0.06] bg-ivory-50/80 px-6 py-14 text-ink shadow-[0_30px_70px_-50px_rgba(43,38,32,0.35)] sm:px-12 lg:px-20 lg:py-20">
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(224,122,85,0.16),transparent_70%)]" />
        <svg aria-hidden viewBox="0 0 400 200" className="pointer-events-none absolute -bottom-6 right-[-40px] w-[340px] text-coral/30 sm:w-[460px]">
          {[60, 95, 130, 165].map((r) => (
            <path key={r} d={`M ${200 - r} 200 A ${r} ${r} 0 0 1 ${200 + r} 200`} fill="none" stroke="currentColor" strokeWidth="1" />
          ))}
        </svg>
        <div className="relative grid gap-10 lg:grid-cols-2 lg:items-center lg:gap-16">
          <div>
            <Eyebrow>Newsletter</Eyebrow>
            <h2 id="newsletter-title" className="mt-4 font-display text-[2.4rem] font-light leading-[1.05] sm:text-5xl">
              Stay Connected
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink-soft">
              Get updates on classes, events and new offerings—plus simple practices to help you stay present in your day-to-day life.
            </p>
          </div>

          <div className="min-h-[132px]">
            <AnimatePresence mode="wait" initial={false}>
              {done ? (
                <motion.div
                  key="done"
                  role="status"
                  data-testid="newsletter-success-message"
                  initial={reduce ? false : { opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-4 rounded-[1.5rem] border border-ink/10 bg-sand-light px-6 py-6"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-moss text-ivory">
                    <Check className="h-5 w-5" />
                  </span>
                  <p className="font-display text-2xl text-ink">{THANKS}</p>
                </motion.div>
              ) : (
                <motion.form key="form" onSubmit={submit} noValidate exit={reduce ? undefined : { opacity: 0, y: -8 }} data-testid="newsletter-form">
                  <label htmlFor="newsletter-email" className="text-[11px] font-semibold uppercase tracking-[0.28em] text-ink-soft">
                    Email address
                  </label>
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                    <input
                      id="newsletter-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError("");
                      }}
                      aria-invalid={!!error}
                      aria-describedby={error ? "newsletter-error" : undefined}
                      data-testid="newsletter-email-input"
                      className={`h-14 w-full min-w-0 shrink-0 rounded-full border bg-white/80 px-6 text-base text-ink placeholder:text-ink/45 transition-[border-color,box-shadow] duration-300 focus:outline-none focus-visible:border-ink/40 focus-visible:ring-2 focus-visible:ring-coral/40 sm:flex-1 ${error ? "border-coral-ink ring-2 ring-coral-ink/30" : "border-ink/15"}`}
                    />
                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="newsletter-submit-button"
                      className="h-14 shrink-0 rounded-full bg-ink px-8 text-sm font-semibold tracking-wide text-ivory transition-colors duration-300 hover:bg-ocean-deep active:scale-[0.98] disabled:opacity-70"
                    >
                      {busy ? "Subscribing…" : "Subscribe"}
                    </button>
                  </div>
                  <div aria-hidden className="absolute left-[-10000px] top-auto h-px w-px overflow-hidden">
                    <label htmlFor="newsletter-company">Company</label>
                    <input id="newsletter-company" name="company" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} data-testid="newsletter-honeypot" />
                  </div>
                  <p id="newsletter-error" role="alert" data-testid="newsletter-error-message" className={`mt-3 min-h-[20px] text-sm font-medium text-coral-ink ${error ? "" : "invisible"}`}>
                    {error}
                  </p>
                  <p className="mt-1 text-xs text-ink-soft" data-testid="newsletter-consent-text">Occasional notes from Jake. Unsubscribe anytime.</p>
                  <p className="mt-1 text-xs text-ink-soft" data-testid="newsletter-data-note">Your email is used only to send you Jake’s newsletter. Every email includes a one-click unsubscribe link.</p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
